import {
  Textarea as HeadlessTextarea,
  type TextareaProps as HeadlessTextareaProps,
} from "@headlessui/react";
import classNames from "classnames";
import {
  type ComponentRef,
  type ElementType,
  type JSX,
  type RefAttributes,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import { useEventCallback } from "@/hooks/useEventCallback";
import { cn } from "@/lib/utils";

import Description from "./Description";
import { FieldContext } from "./FieldContext";

export type TextareaProps<T extends ElementType = "textarea"> = {
  error?: string;
  description?: React.ReactNode;
  visibleLine?: number;
  autoHeight?: boolean;
  onAutoHeight?: (height: number) => void;
} & HeadlessTextareaProps<T>;

const sizes = {
  sm: "py-[7px] px-3 text-sm leading-6",
  md: "py-[9px] px-3.5 text-base",
};

const Textarea = forwardRef(function Textarea(
  {
    className,
    error,
    description,
    autoHeight,
    onAutoHeight,
    visibleLine = 4,
    ...props
  }: TextareaProps,
  ref: React.Ref<HTMLElement>,
) {
  const innerRef = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle(ref, () => innerRef.current!);

  const eventOnAutoHeight = useEventCallback(onAutoHeight);

  useEffect(() => {
    if (!innerRef.current || !autoHeight) return;
    innerRef.current.style.height = "0px";
    const scrollHeight = innerRef.current.scrollHeight + 2; // border
    innerRef.current.style.height = scrollHeight + "px";
    eventOnAutoHeight?.(scrollHeight);
  }, [props.value, autoHeight, eventOnAutoHeight]);

  const fieldContext = useContext(FieldContext);

  return (
    <div
      className={classNames(
        fieldContext.grid ? "sm:col-span-2" : undefined,
        "flex w-full flex-col",
      )}
    >
      <HeadlessTextarea
        className={cn(
          "w-full resize-none rounded-lg border text-gray-900 shadow-xs placeholder:text-gray-500 focus:ring-4 focus:outline-hidden",
          !props.readOnly &&
            (error
              ? "border-error-300 focus:ring-error-100"
              : "focus:border-primary-300 focus:ring-primary-100 border-gray-300"),
          sizes.md,
          "read-only:cursor-default read-only:rounded-lg read-only:border-transparent read-only:bg-gray-50 read-only:shadow-none read-only:focus:ring-0",
          "disabled:cursor-not-allowed disabled:rounded-lg disabled:border disabled:border-gray-300 disabled:bg-gray-50 disabled:text-gray-500",
          className,
        )}
        style={
          visibleLine
            ? { minHeight: `calc(${1.25 + 1.5 * visibleLine}rem)` }
            : {}
        }
        ref={innerRef}
        {...(props as any)}
      />
      {(error || description) && (
        <Description variant={error ? "error" : "hint"} className="mt-1.5">
          {error || description}
        </Description>
      )}
    </div>
  );
}) as <TTag extends ElementType = "textarea">(
  props: TextareaProps<TTag> & RefAttributes<ComponentRef<TTag>>,
) => JSX.Element;

export default Textarea;
